import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import showToast from '../../components/Toast';
import { usePermissions } from '../../contexts/PermissionContext';
import { 
  FiMail, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiSend, 
  FiEye, 
  FiBarChart2,
  FiUsers,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiRefreshCw,
  FiFilter,
  FiSearch,
  FiUser,
  FiAward,
  FiFileText,
  FiTrendingUp,
  FiMousePointer,
  FiCalendar
} from 'react-icons/fi';
import axiosInstance from '../../AxiosInstance';
import EnhancedUniversalTable from '../../components/EnhancedUniversalTable';
import UniversalTable from '../../components/UniversalTable';
import CustomDropdown from '../../components/CustomDropdown';
import EmailMasterLoader from '../../components/EmailMasterLoader';
import './EmailCampaign.css';
import globalRequestManager from '../../utils/globalRequestManager';

const EmailCampaign = () => {
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showBatchSelector, setShowBatchSelector] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [batches, setBatches] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [batchSearch, setBatchSearch] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateFilter, setTemplateFilter] = useState('all');
  const [templateSort, setTemplateSort] = useState('newest');
  const [favoriteTemplates, setFavoriteTemplates] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [previewTemplateData, setPreviewTemplateData] = useState(null);
  const [templateCategories, setTemplateCategories] = useState([]);
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [showReusableTable, setShowReusableTable] = useState(false);
  // Pagination and sorting state (like Contact page)
  const [search, setSearch] = useState("");
  const [countData, setCountData] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  // Sorting State
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt", // Default sort field
    direction: "desc", // Default sort order (newest first)
    field: "createdAt", // Database field name
    order: "desc", // Database sort order
    columnIndex: 3, // "Created" column index (0-based)
  });

  const [filters, setFilters] = useState({
    status: '',
    created: '',
    openRate: '',
    clickRate: ''
  });
  

  // Form state for creating/editing campaigns
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    recipients: [{ email: '', name: '' }],
    scheduleDate: '',
    isManual: true,
    isScheduled: false,
    settings: {
      trackOpens: true,
      trackClicks: true,
      fromName: '',
      fromEmail: '',
      replyTo: ''
    },
    tags: []
  });

  useEffect(() => {
    fetchCampaigns();
    fetchStats();
  }, []);

  // Handle campaign details modal from search bar navigation
  useEffect(() => {
    const openCampaignId = location.state?.openCampaignId;
    const searchTermFromState = location.state?.searchTerm;
    
    if (openCampaignId) {
      // Clear location state immediately to prevent re-opening
      window.history.replaceState({}, document.title);
      
      const openCampaignDetails = async () => {
        // Check if user has permission to view campaigns
        if (!hasPermission('canViewEmailCampaigns')) {
          showToast.error('You do not have permission to view campaign details');
          return;
        }
        
        try {
          const campaignResponse = await axiosInstance.get(`/campaigns/${openCampaignId}`);
          if (campaignResponse.data?.data) {
            setSelectedCampaign(campaignResponse.data.data);
            setShowDetailsModal(true);
          } else if (campaignResponse.data) {
            setSelectedCampaign(campaignResponse.data);
            setShowDetailsModal(true);
          }
          
          // Set search term if provided
          if (searchTermFromState) {
            setSearch(searchTermFromState);
          }
        } catch (error) {
          console.error('Error fetching campaign details:', error);
          showToast.error('Failed to load campaign details');
        }
      };
      openCampaignDetails();
    }
    // Only depend on location.key to detect navigation changes, not showDetailsModal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    fetchCampaigns();
  }, [
    rowsPerPage,
    page,
    search,
    sortConfig,
    filters.status,
    filters.created,
    filters.openRate,
    filters.clickRate
  ]);
  

  const fetchCampaigns = async () => {
    // Check if user has permission to view campaigns
    if (!hasPermission('canViewEmailCampaigns')) {
      setLoading(false);
      setCampaigns([]);
      setCountData(0);
      showToast.error('You do not have permission to view email campaigns');
      return;
    }

    // Add minimum loading time to ensure loader shows properly
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      setLoading(true);
      setCampaigns([]); // Clear campaigns data when loading starts 
      
      const res = await axiosInstance.get('/campaigns', {
        params: {
          pageSize: rowsPerPage,
          pageNumber: page + 1,
          search: search || "",
          sortField: sortConfig.field,
          sortOrder: sortConfig.order,
      
          // ✅ filters
          status: filters.status || "",
          created: filters.created || "",
          openRate: filters.openRate || "",
          clickRate: filters.clickRate || ""
        }
      });
      
      
      if (res && res.status === 200) {
        setCampaigns(res?.data?.data || []);
        setCountData(res?.data?.totalCount || 0);
      } else if (res && res.status === 204) {
        setCampaigns([]);
        setCountData(res?.data?.totalCount || 0);
      } else {
        setCampaigns([]);
        setCountData(0);
      }
    } catch (error) {
      console.error("Error fetching campaign data:", error.message || error);
      setCampaigns([]); // Ensure campaigns is set to empty array on error
    } finally {
      // Wait for minimum loading time before setting loading to false
      await minLoadingTime;
      setLoading(false);
      setHasLoaded(true);
    }
  };

  const handleSort = (columnKey) => {
    
    // Map column keys to database fields
    const fieldMap = {
      "name": "name",
      "subject": "subject", 
      "status": "status",
      "createdAt": "createdAt",
      "totalRecipients": "totalRecipients",
      "openRate": "openRate",
      "clickRate": "clickRate"
    };

    const field = fieldMap[columnKey];
    
    if (!field) {
      return;
    }

    const isAsc =
      sortConfig.field === field && sortConfig.order === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    // Find column index for highlighting
    const columnIndex = Object.keys(fieldMap).indexOf(columnKey);

    setSortConfig({
      key: columnKey,
      direction: newOrder,
      field: field,
      order: newOrder,
      columnIndex: columnIndex,
    });

    // Reset to page 1 when sorting to avoid confusion
    setPage(0);
  };

  const resetSort = () => {
    setSortConfig({
      field: "createdAt",
      order: "desc",
      columnIndex: 3, // Highlight "Created" column (0-based index)
    });
    setPage(0);
  };

  const indexOfFirstItem = page * rowsPerPage;
  const currentData = campaigns; // Data is already paginated and sorted by the backend

  const fetchStats = async () => {
    // Check if user has permission to view campaigns
    if (!hasPermission('canViewEmailCampaigns')) {
      return;
    }
    
    const url = '/campaigns/stats';
    
    const result = await globalRequestManager.executeRequest(
      async () => {
        const response = await axiosInstance.get(url);
        if (response && response.data) {
          setStats(response.data);
        }
        return response;
      },
      'GET',
      url
    );
    
    if (result === null) {
      return;
    }
  };

  const fetchBatches = async (search = '') => {
    try {
      setLoadingBatches(true);
      const params = new URLSearchParams({
        search,
        pageSize: 100,
        pageNumber: 1,
        sortField: 'createdAt',
        sortOrder: 'desc'
      });
      
      const response = await axiosInstance.get(`/email-batches?${params}`);
      if (response && response.data && response.data.data) {
        setBatches(response.data.data);
      } else if (response && response.data) {
        setBatches([]);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      showToast.error('Failed to fetch batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchTemplates = async (search = '') => {
    try {
      setLoadingTemplates(true);
      
      // Get user ID from multiple possible sources
      let userId = localStorage.getItem('userId') || 
                   localStorage.getItem('superadminId') || 
                   localStorage.getItem('id') ||
                   localStorage.getItem('UserId');
      
      // If still no userId, show error
      if (!userId) {
        showToast.error('User ID not found. Please log in again.');
        setLoadingTemplates(false);
        return;
      }
      
      
      const params = new URLSearchParams({
        search,
        limit: 100,
        includeContent: 'true' // Request full content
      });
      
      const response = await axiosInstance.get(`/templates/user/${userId}?${params}`);
      
      // Filter to show templates (prefer Modern Email Builder but show all if needed)
      const allTemplates = (response && response.data && response.data.data) ? response.data.data : [];
      
      // First try to get Modern Email Builder templates
      let modernTemplates = allTemplates.filter(template => {
        // Include templates that have JSON sections (Modern Email Builder)
        const hasJsonSections = template.json && template.json.sections && template.json.sections.length > 0;
        // Include templates with any HTML content
        const hasHtml = template.html && template.html.trim().length > 0;
        // Include templates with any content
        const hasContent = template.content && template.content.trim().length > 0;
        
        // Include if it has JSON sections OR any content
        return hasJsonSections || hasHtml || hasContent;
      });
      
      // If no modern templates found, show all templates (fallback)
      if (modernTemplates.length === 0) {
        modernTemplates = allTemplates;
      }
      
      
      setTemplates(modernTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showToast.error('Failed to fetch templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const createTestTemplate = async () => {
    try {
      // Get user ID from multiple possible sources
      let userId = localStorage.getItem('userId') || 
                   localStorage.getItem('superadminId') || 
                   localStorage.getItem('id') ||
                   localStorage.getItem('UserId');
      
      // If still no userId, show error
      if (!userId) {
        showToast.error('User ID not found. Please log in again.');
        return;
      }
      
      
      const testTemplate = {
        userId: userId,
        templateName: 'Test Email Template with Image',
        html: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background: #ffffff;">
            <div style="text-align: center; margin-bottom: 30px;">
              <img src="https://via.placeholder.com/400x200/007bff/ffffff?text=Your+Logo" 
                   alt="Company Logo" 
                   style="max-width: 200px; height: auto; border-radius: 8px;">
            </div>
            
            <h1 style="color: #333; text-align: center; margin-bottom: 20px;">Welcome to Our Newsletter</h1>
            
            <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
              This is a test email template that you can use for your email campaigns.
              You can customize this content to match your brand and messaging.
            </p>
            
            <div style="text-align: center; margin: 30px 0;">
              <img src="https://via.placeholder.com/500x300/28a745/ffffff?text=Featured+Image" 
                   alt="Featured Content" 
                   style="max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 4px 8px rgba(0,0,0,0.1);">
            </div>
            
            <p style="color: #666; line-height: 1.6; margin-bottom: 30px;">
              This template includes images to test the image display functionality.
              The images should appear properly when you preview or use this template.
            </p>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="#" style="background: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; display: inline-block;">
                Call to Action
              </a>
            </div>
            
            <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin-top: 30px;">
              <h3 style="color: #333; margin-bottom: 15px;">Template Features:</h3>
              <ul style="color: #666; line-height: 1.6;">
                <li>✅ Responsive design</li>
                <li>✅ Image support</li>
                <li>✅ Professional styling</li>
                <li>✅ Easy customization</li>
              </ul>
            </div>
            
            <p style="color: #999; font-size: 14px; text-align: center; margin-top: 30px;">
              © 2024 Your Company. All rights reserved.
            </p>
          </div>
        `,
        json: {
          sections: [
            {
              id: 1,
              type: 'header',
              content: { title: 'Welcome to Our Newsletter', subtitle: 'Stay updated with our latest news' },
              styles: { backgroundColor: '#ffffff', textAlign: 'center', padding: '40px 20px' }
            },
            {
              id: 2,
              type: 'image',
              content: { 
                src: 'https://via.placeholder.com/400x200/007bff/ffffff?text=Your+Logo',
                alt: 'Company Logo'
              },
              styles: { textAlign: 'center', padding: '20px' }
            },
            {
              id: 3,
              type: 'text',
              content: { text: 'This is a test email template that you can use for your email campaigns.' },
              styles: { backgroundColor: '#ffffff', padding: '20px' }
            },
            {
              id: 4,
              type: 'image',
              content: { 
                src: 'https://via.placeholder.com/500x300/28a745/ffffff?text=Featured+Image',
                alt: 'Featured Content'
              },
              styles: { textAlign: 'center', padding: '20px' }
            }
          ]
        }
      };
      
      
      const response = await axiosInstance.post('/templates', testTemplate);
      
      if (response && response.data && response.data.statusCode === 201) {
        showToast.success('Test template created successfully!');
        // Refresh templates list
        fetchTemplates();
      } else {
        console.error('Template creation failed:', response?.data);
        showToast.error('Failed to create template: ' + (response?.data?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating test template:', error);
      showToast.error('Failed to create test template');
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    
    // Check if user has permission to create campaigns
    if (!hasPermission('canCreateEmailCampaigns')) {
      showToast.error('You do not have permission to create campaigns');
      return;
    }
    
    try {
      
      // If content is too short, show warning
      if (formData.content.length < 10) {
        showToast.warning('Email content seems too short. Please check if template was selected properly.');
      }
      
      const response = await axiosInstance.post('/campaigns', formData);
      showToast.success('Campaign created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      console.error('Error creating campaign:', error);
      showToast.error(error.response?.data?.error || 'Failed to create campaign');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    // Check if user has permission to create campaigns (send requires create permission)
    if (!hasPermission('canCreateEmailCampaigns')) {
      showToast.error('You do not have permission to send campaigns');
      return;
    }
    
    try {
      await axiosInstance.post(`/campaigns/${campaignId}/send`);
      showToast.success('Campaign sending started');
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      console.error('Error sending campaign:', error);
      showToast.error(error.response?.data?.error || 'Failed to send campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    // Check if user has permission to delete campaigns
    if (!hasPermission('canDeleteEmailCampaigns')) {
      showToast.error('You do not have permission to delete campaigns');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/campaigns/${campaignId}`);
      showToast.success('Campaign deleted successfully');
      fetchCampaigns();
      fetchStats();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      showToast.error(error.response?.data?.error || 'Failed to delete campaign');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      content: '',
      recipients: [{ email: '', name: '' }],
      scheduleDate: '',
      isManual: true,
      isScheduled: false,
      settings: {
        trackOpens: true,
        trackClicks: true,
        fromName: '',
        fromEmail: '',
        replyTo: ''
      },
      tags: []
    });
    setSelectedTemplate(null);
  };

  const addRecipient = () => {
    setFormData(prev => ({
      ...prev,
      recipients: [...prev.recipients, { email: '', name: '' }]
    }));
  };

  const removeRecipient = (index) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter((_, i) => i !== index)
    }));
  };

  const updateRecipient = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.map((recipient, i) => 
        i === index ? { ...recipient, [field]: value } : recipient
      )
    }));
  };

  // Batch selection functions
  const selectBatch = (batch) => {
    // Check if batch is already selected
    const isAlreadySelected = selectedBatches.some(b => b.batchId === batch.batchId || b._id === batch._id);
    if (isAlreadySelected) {
      showToast.warning(`${batch.batchedName} is already selected`);
      return;
    }

    setSelectedBatches(prev => [...prev, batch]);
    showToast.success(`${batch.batchedName} selected`);
  };

  const removeSelectedBatch = (batchId) => {
    setSelectedBatches(prev => prev.filter(b => (b.batchId !== batchId && b._id !== batchId)));
  };

  const addSelectedBatchesToRecipients = () => {
    if (selectedBatches.length === 0) {
      showToast.warning('Please select at least one batch');
      return;
    }

    // Extract all emails from selected batches
    const newRecipients = [];
    selectedBatches.forEach(batch => {
      if (batch.personDetails && Array.isArray(batch.personDetails)) {
        batch.personDetails.forEach(person => {
          // Check if email already exists in current recipients
          const emailExists = formData.recipients.some(r => r.email && r.email.toLowerCase() === person.email.toLowerCase());
          if (!emailExists && person.email) {
            newRecipients.push({
              email: person.email.toLowerCase(),
              name: person.firstName && person.lastName 
                ? `${person.firstName} ${person.lastName}`.trim()
                : person.firstName || person.lastName || ''
            });
          }
        });
      }
    });

    if (newRecipients.length === 0) {
      showToast.warning('No new recipients found in selected batches');
      return;
    }

    setFormData(prev => ({
      ...prev,
      recipients: [...prev.recipients.filter(r => r.email), ...newRecipients]
    }));
    
    setSelectedBatches([]);
    setShowBatchSelector(false);
    setBatchSearch('');
    showToast.success(`${newRecipients.length} recipient(s) added from ${selectedBatches.length} batch(es)`);
  };

  const convertJsonToHtml = (jsonData) => {
    if (!jsonData.sections || !Array.isArray(jsonData.sections)) {
      return '';
    }
    
    let html = '<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">';
    
    jsonData.sections.forEach(section => {
      const styles = section.styles || {};
      const content = section.content || {};
      
      let sectionStyle = '';
      Object.keys(styles).forEach(key => {
        if (key === 'backgroundColor') sectionStyle += `background-color: ${styles[key]}; `;
        if (key === 'textAlign') sectionStyle += `text-align: ${styles[key]}; `;
        if (key === 'padding') sectionStyle += `padding: ${styles[key]}; `;
        if (key === 'color') sectionStyle += `color: ${styles[key]}; `;
        if (key === 'fontSize') sectionStyle += `font-size: ${styles[key]}; `;
      });
      
      html += `<div style="${sectionStyle}">`;
      
      switch (section.type) {
        case 'header':
          html += `<h1 style="margin: 0; line-height: 1.6;">${content.title || ''}</h1>`;
          if (content.subtitle) {
            html += `<p style="margin: 10px 0 0 0; line-height: 1.6;">${content.subtitle}</p>`;
          }
          break;
        case 'text':
          html += `<p style="margin: 0; line-height: 1.6;">${content.text || ''}</p>`;
          break;
        case 'image':
          if (content.src) {
            html += `<img src="${content.src}" alt="${content.alt || ''}" style="max-width: 100%; height: auto; border-radius: 4px;">`;
          }
          break;
        case 'button':
          html += `<a href="${content.url || '#'}" style="display: inline-block; padding: 12px 24px; background: #007bff; color: white; text-decoration: none; border-radius: 5px;">${content.text || 'Click Here'}</a>`;
          break;
        case 'divider':
          html += `<hr style="border: none; border-top: 1px solid #e0e0e0; margin: 20px 0;">`;
          break;
        case 'footer':
          html += `<p style="margin: 0; line-height: 1.6;">${content.text || ''}</p>`;
          break;
        default:
          html += `<p style="margin: 0; line-height: 1.6;">${content.text || ''}</p>`;
      }
      
      html += '</div>';
    });
    
    html += '</div>';
    return html;
  };

  // Load full template payload if the list endpoint returned a slim document
  const loadFullTemplate = async (templateRef) => {
    try {
      // Check if we already have the full content (html or content with actual data)
      // Don't consider empty strings as having content
      const hasHtmlContent = templateRef?.html && typeof templateRef.html === 'string' && templateRef.html.trim().length > 0;
      const hasTextContent = templateRef?.content && typeof templateRef.content === 'string' && templateRef.content.trim().length > 0;
      const hasJsonSections = templateRef?.json && 
                               typeof templateRef.json === 'object' && 
                               templateRef.json !== null &&
                               templateRef.json.sections && 
                               Array.isArray(templateRef.json.sections) &&
                               templateRef.json.sections.length > 0;
      
      if (hasHtmlContent || hasTextContent || hasJsonSections) {
        return templateRef;
      }

      // Prefer custom TemplateId if available
      if (templateRef?.TemplateId) {
        try {
          const res = await axiosInstance.get(`/templates/by-template-id/${templateRef.TemplateId}`);
          if (res.data?.statusCode === 200 && res.data?.data) {
            return res.data.data;
          }
        } catch (err) {
          // Error loading template by TemplateId
        }
      }

      // Fallback to Mongo _id
      if (templateRef?._id) {
        try {
          const res = await axiosInstance.get(`/templates/${templateRef._id}`);
          if (res.data?.statusCode === 200 && res.data?.data) {
            return res.data.data;
          }
        } catch (err) {
          // Error loading template by _id
        }
      }

      return templateRef; // return as-is if nothing else worked
    } catch (err) {
      showToast.error('Failed to load template content');
      return templateRef;
    }
  };

  // Enhanced template management functions
  const toggleFavorite = (templateId) => {
    setFavoriteTemplates(prev => {
      const isFavorite = prev.includes(templateId);
      if (isFavorite) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };


  const getFilteredAndSortedTemplates = () => {
    let filtered = [...templates];

    // Apply search filter
    if (templateSearch) {
      filtered = filtered.filter(template =>
        template.templateName.toLowerCase().includes(templateSearch.toLowerCase()) ||
        (template.content && template.content.toLowerCase().includes(templateSearch.toLowerCase())) ||
        (template.html && template.html.toLowerCase().includes(templateSearch.toLowerCase()))
      );
    }

    // Apply category filter
    if (templateFilter !== 'all') {
      filtered = filtered.filter(template => {
        switch (templateFilter) {
          case 'html':
            return template.html;
          case 'text':
            return !template.html && template.content;
          case 'favorites':
            return favoriteTemplates.includes(template.TemplateId || template._id);
          case 'recent':
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return new Date(template.createdAt) > weekAgo;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (templateSort) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name':
          return a.templateName.localeCompare(b.templateName);
        case 'favorites':
          const aIsFavorite = favoriteTemplates.includes(a.TemplateId || a._id);
          const bIsFavorite = favoriteTemplates.includes(b.TemplateId || b._id);
          if (aIsFavorite && !bIsFavorite) return -1;
          if (!aIsFavorite && bIsFavorite) return 1;
          return a.templateName.localeCompare(b.templateName);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const selectTemplate = async (template) => {
    try {
      setSelectedTemplate(template);
      
      console.log('Selecting template:', { 
        TemplateId: template.TemplateId, 
        _id: template._id,
        hasHtml: !!template.html,
        htmlLength: template.html?.length || 0,
        hasContent: !!template.content,
        contentLength: template.content?.length || 0,
        hasJson: !!template.json
      });
      
      // Ensure we have full template content (superadmin list may omit html/json)
      const fullTemplate = await loadFullTemplate(template);
      
      console.log('Full template loaded:', {
        hasHtml: !!fullTemplate.html,
        htmlLength: fullTemplate.html?.length || 0,
        htmlIsEmpty: fullTemplate.html ? fullTemplate.html.trim().length === 0 : true,
        hasContent: !!fullTemplate.content,
        contentLength: fullTemplate.content?.length || 0,
        contentIsEmpty: fullTemplate.content ? fullTemplate.content.trim().length === 0 : true,
        hasJson: !!fullTemplate.json,
        jsonType: typeof fullTemplate.json
      });

      // Process the template content to fix image URLs
      let processedContent = '';
      
      // Parse JSON if it's a string
      let jsonData = fullTemplate.json;
      if (jsonData && typeof jsonData === 'string') {
        try {
          jsonData = JSON.parse(jsonData);
        } catch (e) {
          console.warn('Failed to parse JSON string:', e);
          jsonData = null;
        }
      }
      
      // Try different possible content sources in priority order
      // Priority 1: HTML field (should be the primary source for BuilderJS templates)
      if (fullTemplate.html && typeof fullTemplate.html === 'string' && fullTemplate.html.trim().length > 0) {
        processedContent = fullTemplate.html.trim();
        console.log('✓ Using HTML content, length:', processedContent.length);
      } 
      // Priority 2: Content field (fallback)
      else if (fullTemplate.content && typeof fullTemplate.content === 'string' && fullTemplate.content.trim().length > 0) {
        processedContent = fullTemplate.content.trim();
        console.log('✓ Using content field, length:', processedContent.length);
      } 
      // Priority 3: Try to convert JSON (only for simple sections structure, not BuilderJS elementLists)
      else if (jsonData && typeof jsonData === 'object' && jsonData !== null) {
        // Check if it's the simple sections structure
        if (jsonData.sections && Array.isArray(jsonData.sections) && jsonData.sections.length > 0) {
          processedContent = convertJsonToHtml(jsonData);
          console.log('✓ Converted JSON sections to HTML, length:', processedContent.length);
        } 
        // BuilderJS structure with elementLists - we can't easily convert this without BuilderJS instance
        // So we should show an error and suggest the template needs to be saved with HTML
        else if (jsonData.elementLists && Array.isArray(jsonData.elementLists)) {
          console.error('Template has BuilderJS elementLists structure but no HTML field. Template needs to be saved with HTML content.');
          showToast.error('This template format requires HTML content. Please ensure the template was saved with HTML content, or select a different template.');
          return;
        } else {
          console.error('Template JSON structure is not recognized:', Object.keys(jsonData));
          showToast.error('Template format not supported. Please select a different template.');
          return;
        }
      } 
      // No valid content found
      else {
        console.error('No valid content found in template:', {
          html: fullTemplate.html ? (fullTemplate.html.trim().length > 0 ? `exists (${fullTemplate.html.length} chars)` : 'exists but empty') : 'missing',
          content: fullTemplate.content ? (fullTemplate.content.trim().length > 0 ? `exists (${fullTemplate.content.length} chars)` : 'exists but empty') : 'missing',
          json: jsonData ? (jsonData.sections ? `has ${jsonData.sections.length} sections` : jsonData.elementLists ? `has ${jsonData.elementLists.length} elementLists` : 'unknown structure') : 'missing'
        });
        showToast.error('Template has no content. Please select a different template or ensure the template was saved with HTML content.');
        return;
      }
      
      // Validate that we have actual content
      if (!processedContent || processedContent.trim().length === 0) {
        console.error('Processed content is empty after extraction');
        showToast.error('Template content is empty. Please select a different template.');
        return;
      }
      
      const baseURL = 'http://localhost:5000'; // Fixed base URL
      
      // Handle various image URL patterns
      // 1. Relative uploads paths
      processedContent = processedContent.replace(
        /src="(\/uploads\/[^"]+)"/g, 
        `src="${baseURL}$1"`
      );
      processedContent = processedContent.replace(
        /src="(uploads\/[^"]+)"/g, 
        `src="${baseURL}/$1"`
      );
      
      // 2. CDN paths
      processedContent = processedContent.replace(
        /src="(\/cdn\/[^"]+)"/g, 
        `src="${baseURL}$1"`
      );
      processedContent = processedContent.replace(
        /src="(cdn\/[^"]+)"/g, 
        `src="${baseURL}/$1"`
      );
      
      // 3. API paths
      processedContent = processedContent.replace(
        /src="(\/v1\/cdn\/[^"]+)"/g, 
        `src="${baseURL}$1"`
      );
      processedContent = processedContent.replace(
        /src="(v1\/cdn\/[^"]+)"/g, 
        `src="${baseURL}/$1"`
      );
      
      // 4. Handle any other relative paths that might be missing protocol
      processedContent = processedContent.replace(
        /src="(?!http|https|data:)([^"]+)"/g, 
        `src="${baseURL}/$1"`
      );
      
      console.log('Final processed content length:', processedContent.length);
      console.log('Content preview (first 200 chars):', processedContent.substring(0, 200));
      
      // Set the content in formData
      setFormData(prev => {
        const newFormData = {
          ...prev,
          content: processedContent
        };
        console.log('✓ Updated formData with content, new content length:', newFormData.content.length);
        return newFormData;
      });
      
      setShowTemplateSelector(false);
      showToast.success(`Template "${fullTemplate.templateName || template.templateName}" selected and content loaded`);
    } catch (error) {
      console.error('Error selecting template:', error);
      console.error('Error stack:', error.stack);
      showToast.error('Failed to load template content. Please try again.');
    }
  };

  const previewTemplate = async (template) => {
    // Create a modal to preview the template
    const previewWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the base URL for images from config
    const baseURL = 'http://localhost:5000'; // Default API URL
    
    // Process HTML content to fix image URLs
    const fullTemplate = await loadFullTemplate(template);
    let processedHtml = fullTemplate.html || fullTemplate.content || (
      fullTemplate.json ? convertJsonToHtml(fullTemplate.json) : 'No content available'
    );
    
    
    // Handle various image URL patterns
    // 1. Relative uploads paths
    processedHtml = processedHtml.replace(
      /src="(\/uploads\/[^"]+)"/g, 
      `src="${baseURL}$1"`
    );
    processedHtml = processedHtml.replace(
      /src="(uploads\/[^"]+)"/g, 
      `src="${baseURL}/$1"`
    );
    
    // 2. CDN paths
    processedHtml = processedHtml.replace(
      /src="(\/cdn\/[^"]+)"/g, 
      `src="${baseURL}$1"`
    );
    processedHtml = processedHtml.replace(
      /src="(cdn\/[^"]+)"/g, 
      `src="${baseURL}/$1"`
    );
    
    // 3. API paths
    processedHtml = processedHtml.replace(
      /src="(\/v1\/cdn\/[^"]+)"/g, 
      `src="${baseURL}$1"`
    );
    processedHtml = processedHtml.replace(
      /src="(v1\/cdn\/[^"]+)"/g, 
      `src="${baseURL}/$1"`
    );
    
    // 4. Handle data URLs and external URLs (leave them as is)
    // 5. Handle any other relative paths that might be missing protocol
    processedHtml = processedHtml.replace(
      /src="(?!http|https|data:)([^"]+)"/g, 
      `src="${baseURL}/$1"`
    );
    
    
    previewWindow.document.write(`
      <html>
        <head>
          <title>Template Preview: ${template.templateName}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background: #f9f9f9;
            }
            .preview-header { 
              background: #ffffff; 
              padding: 20px; 
              margin-bottom: 20px; 
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .preview-content { 
              background: #ffffff;
              border: 1px solid #e0e0e0; 
              padding: 30px; 
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            img { 
              max-width: 100%; 
              height: auto; 
              border-radius: 4px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .image-debug {
              background: #f0f0f0;
              padding: 10px;
              margin: 10px 0;
              border-radius: 4px;
              font-size: 12px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="preview-header">
            <h2>Template Preview: ${template.templateName}</h2>
            <p><strong>Created:</strong> ${new Date(template.createdAt).toLocaleDateString()}</p>
            <p><strong>Base URL:</strong> ${baseURL}</p>
          </div>
          <div class="preview-content">
            ${processedHtml}
          </div>
          <script>
            document.addEventListener('DOMContentLoaded', function() {
              const images = document.querySelectorAll('img');
              images.forEach((img) => {
                img.onerror = function() {
                  console.error('Failed to load image:', img.src);
                  this.style.border = '2px solid red';
                  this.style.padding = '10px';
                  this.alt = 'Image failed to load: ' + img.src;
                };
              });
            });
          </script>
        </body>
      </html>
    `);
    previewWindow.document.close();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'text-gray-500';
      case 'scheduled': return 'text-blue-500';
      case 'sending': return 'text-yellow-500';
      case 'sent': return 'text-green-500';
      case 'cancelled': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  // Enhanced rate calculation functions for email campaign analytics
  const calculateOpenRate = (campaign) => {
    if (!campaign) return 0;
    
    // Use analytics data if available (most accurate)
    if (analyticsData && analyticsData.openRate !== undefined && analyticsData.openRate !== null) {
      return parseFloat(analyticsData.openRate).toFixed(1);
    }
    
    // Use campaign-level data if available, otherwise calculate from recipients
    if (campaign.openRate !== undefined && campaign.openRate !== null) {
      return parseFloat(campaign.openRate).toFixed(1);
    }
    
    if (!campaign.recipients || campaign.recipients.length === 0) return 0;
    
    const sentRecipients = campaign.recipients.filter(r => r.status === 'sent');
    if (sentRecipients.length === 0) return 0;
    
    const totalOpens = campaign.openCount || sentRecipients.reduce((sum, r) => sum + (r.openCount || 0), 0);
    const rate = (totalOpens / sentRecipients.length) * 100;
    return rate.toFixed(1);
  };

  const calculateClickRate = (campaign) => {
    if (!campaign) return 0;
    
    // Use analytics data if available (most accurate)
    if (analyticsData && analyticsData.clickRate !== undefined && analyticsData.clickRate !== null) {
      return parseFloat(analyticsData.clickRate).toFixed(1);
    }
    
    // Use campaign-level data if available, otherwise calculate from recipients
    if (campaign.clickRate !== undefined && campaign.clickRate !== null) {
      return parseFloat(campaign.clickRate).toFixed(1);
    }
    
    if (!campaign.recipients || campaign.recipients.length === 0) return 0;
    
    const sentRecipients = campaign.recipients.filter(r => r.status === 'sent');
    if (sentRecipients.length === 0) return 0;
    
    const totalClicks = campaign.clickCount || sentRecipients.reduce((sum, r) => sum + (r.clickCount || 0), 0);
    const rate = (totalClicks / sentRecipients.length) * 100;
    return rate.toFixed(1);
  };

  const calculateDeliveryRate = (campaign) => {
    if (!campaign) return 0;
    
    // Use analytics data if available (most accurate)
    if (analyticsData && analyticsData.sentCount !== undefined && analyticsData.totalRecipients !== undefined) {
      const rate = (analyticsData.sentCount / analyticsData.totalRecipients) * 100;
      return rate.toFixed(1);
    }
    
    const totalRecipients = campaign.totalRecipients || (campaign.recipients ? campaign.recipients.length : 0);
    if (totalRecipients === 0) return 0;
    
    const sentCount = campaign.sentCount || (campaign.recipients ? campaign.recipients.filter(r => r.status === 'sent').length : 0);
    const rate = (sentCount / totalRecipients) * 100;
    return rate.toFixed(1);
  };

  const calculateClickToOpenRate = (campaign) => {
    if (!campaign) return 0;
    
    const totalOpens = campaign.openCount || (campaign.recipients ? campaign.recipients.reduce((sum, r) => sum + (r.openCount || 0), 0) : 0);
    if (totalOpens === 0) return 0;
    
    const totalClicks = campaign.clickCount || (campaign.recipients ? campaign.recipients.reduce((sum, r) => sum + (r.clickCount || 0), 0) : 0);
    const rate = (totalClicks / totalOpens) * 100;
    return rate.toFixed(1);
  };

  const calculateRecipientEngagement = (recipient) => {
    if (!recipient) return 0;
    
    const opens = recipient.openCount || 0;
    const clicks = recipient.clickCount || 0;
    
    // Enhanced engagement calculation
    // Opens = 1 point each, Clicks = 3 points each (more valuable)
    const engagementScore = opens + (clicks * 3);
    
    // Convert to percentage (cap at 100%)
    const maxPossibleScore = 10; // Assuming max 10 opens + clicks per recipient
    const percentage = Math.min((engagementScore / maxPossibleScore) * 100, 100);
    
    return percentage.toFixed(0);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <FiEdit />;
      case 'scheduled': return <FiClock />;
      case 'sending': return <FiRefreshCw />;
      case 'sent': return <FiCheckCircle />;
      case 'cancelled': return <FiXCircle />;
      default: return <FiEdit />;
    }
  };

  // Check permission before rendering
  if (!hasPermission('canViewEmailCampaigns')) {
    return (
      <div className="email-campaign-container" style={{ height: '100vh', display: 'flex', flexDirection: 'column', padding: '0 10px', margin: '0', maxWidth: '1500px', marginLeft: 'auto', marginRight: 'auto', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <FiMail style={{ fontSize: '4rem', color: '#9ca3af', marginBottom: '1rem' }} />
          <h2 style={{ color: '#374151', marginBottom: '0.5rem' }}>Access Denied</h2>
          <p style={{ color: '#6b7280' }}>You do not have permission to view email campaigns. Contact your administrator for access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-campaign-container" style={{ height: '100vh', display: 'flex', flexDirection: 'column', padding: '0 10px', margin: '0', maxWidth: '1500px', marginLeft: 'auto', marginRight: 'auto' }}>
      {/* Header */}
      <div className="campaign-header" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '20px',
        background: '#ffffff',
        borderRadius: '8px',
        marginBottom: '10px',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e5e7eb'
      }}>
        <div className="header-left">
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#111827' }}>
            <FiMail style={{ marginRight: '8px', color: '#4f46e5' }} /> 
            Email Campaigns
          </h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{ position: 'relative' }}>
            <FiSearch style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#9ca3af',
              fontSize: '16px'
            }} />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: '8px 12px 8px 40px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                width: '250px',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          {hasPermission('canCreateEmailCampaigns') && (
            <button 
              className="create-campaign-btn"
              onClick={() => setShowCreateModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#4338ca'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#4f46e5'}
            >
              <FiPlus /> Create Campaign
            </button>
          )}
        </div>
      </div>

      {/* Statistics Cards - Hidden to maximize table space */}
      <div className="stats-grid" style={{ display: 'none' }}>
        <div className="stat-card" style={{ padding: '4px', minHeight: '30px' }}>
          <div className="stat-icon">
            <FiMail />
          </div>
          <div className="stat-content">
            <h3 style={{ fontSize: '0.9rem', margin: '0' }}>{stats.totalCampaigns || 0}</h3>
            <p style={{ fontSize: '0.65rem', margin: '0' }}>Total</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <div className="stat-content">
            <h3>{stats.totalRecipients || 0}</h3>
            <p>Total Recipients</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <FiCheckCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.totalSent || 0}</h3>
            <p>Sent</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <FiXCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.totalFailed || 0}</h3>
            <p>Failed</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <FiBarChart2 />
          </div>
          <div className="stat-content">
            <h3>{stats.avgOpenRate ? `${stats.avgOpenRate.toFixed(1)}%` : '0%'}</h3>
            <p>Avg Open Rate</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <FiBarChart2 />
          </div>
          <div className="stat-content">
            <h3>{stats.avgClickRate ? `${stats.avgClickRate.toFixed(1)}%` : '0%'}</h3>
            <p>Avg Click Rate</p>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-left">
          <h3 className="filters-heading">Filters:</h3>
        </div>
        <div className="filters-right">
           <div className="filter-dropdown">
             <FiFilter />
             <CustomDropdown
               options={[
                 { value: '', label: 'All Status' },
                 { value: 'draft', label: 'Draft' },
                 { value: 'scheduled', label: 'Scheduled' },
                 { value: 'sending', label: 'Sending' },
                 { value: 'sent', label: 'Sent' },
                 { value: 'cancelled', label: 'Cancelled' }
               ]}
               value={filters.status ? { value: filters.status, label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1) } : null}
               onChange={(option) => setFilters(prev => ({ ...prev, status: option.value }))}
               placeholder="All Status"
               className="status-dropdown"
               textColor="white"
             />
           </div>
           <div className="filter-dropdown">
             <FiFilter />
             <CustomDropdown
               options={[
                 { value: '', label: 'All Created' },
                 { value: 'today', label: 'Today' },
                 { value: 'week', label: 'This Week' },
                 { value: 'month', label: 'This Month' },
                 { value: 'year', label: 'This Year' }
               ]}
               value={filters.created ? { value: filters.created, label: filters.created } : null}
               onChange={(option) => setFilters(prev => ({ ...prev, created: option.value }))}
               placeholder="All Created"
               className="created-dropdown"
               textColor="white"
             />
           </div>
           <div className="filter-dropdown">
             <FiFilter />
             <CustomDropdown
               options={[
                 { value: '', label: 'All Open Rate' },
                 { value: '0-10', label: '0-10%' },
                 { value: '11-25', label: '11-25%' },
                 { value: '26-50', label: '26-50%' },
                 { value: '50+', label: '50%+' }
               ]}
               value={filters.openRate ? { value: filters.openRate, label: filters.openRate } : null}
               onChange={(option) => setFilters(prev => ({ ...prev, openRate: option.value }))}
               placeholder="All Open Rate"
               className="open-rate-dropdown"
               textColor="white"
             />
           </div>
           <div className="filter-dropdown">
             <FiFilter />
             <CustomDropdown
               options={[
                 { value: '', label: 'All Click Rate' },
                 { value: '0-5', label: '0-5%' },
                 { value: '6-15', label: '6-15%' },
                 { value: '16-30', label: '16-30%' },
                 { value: '30+', label: '30%+' }
               ]}
               value={filters.clickRate ? { value: filters.clickRate, label: filters.clickRate } : null}
               onChange={(option) => setFilters(prev => ({ ...prev, clickRate: option.value }))}
               placeholder="All Click Rate"
               className="click-rate-dropdown"
               textColor="white"
             />
           </div>
         </div>
       </div>

      {/* Campaigns Table */}
      <div style={{ 
        marginTop: '10px',
        background: '#ffffff',
        borderRadius: '8px',
        padding: '0',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        height: 'calc(100vh - 70px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Custom Table Structure */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
              <EnhancedUniversalTable
            data={campaigns}
            columns={[
              {
                key: 'name',
                title: 'Campaign Name',
                type: 'name',
                sortable: true,
                render: (value, row) => (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px'
                  }}>
                    <FiMail style={{ color: '#6b7280', fontSize: '16px' }} />
                    <span style={{ 
                      fontWeight: '500', 
                      color: '#111827',
                      fontSize: '0.9rem'
                    }}>{value}</span>
                  </div>
                )
              },
              {
                key: 'subject',
                title: 'Subject',
                sortable: true,
                render: (value) => (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px'
                  }}>
                    <FiFileText style={{ color: '#6b7280', fontSize: '14px' }} />
                    <span style={{ 
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#111827'
                    }}>{value}</span>
                  </div>
                )
              },
              {
                key: 'status',
                title: 'Status',
                type: 'status',
                sortable: true,
                filterable: true
              },
              {
                key: 'totalRecipients',
                title: 'Recipients',
                type: 'number',
                sortable: true,
                render: (value) => (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px'
                  }}>
                    <FiUsers style={{ color: '#6b7280', fontSize: '14px' }} />
                    <span style={{ 
                      fontWeight: '500',
                      color: '#111827',
                      fontSize: '0.9rem'
                    }}>{value || 0}</span>
                  </div>
                )
              },
              {
                key: 'createdAt',
                title: 'Created',
                type: 'date',
                sortable: true,
                render: (value) => (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px'
                  }}>
                    <FiClock style={{ color: '#6b7280', fontSize: '14px' }} />
                    <span style={{ 
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      color: '#111827'
                    }}>{new Date(value).toLocaleDateString()}</span>
                  </div>
                )
              },
              {
                key: 'openRate',
                title: 'Open Rate',
                sortable: true,
                render: (value, row) => (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px'
                  }}>
                    <FiEye style={{ color: '#6b7280', fontSize: '14px' }} />
                    <span style={{ 
                      color: '#111827', 
                      fontWeight: '500',
                      fontSize: '0.9rem'
                    }}>
                      {calculateOpenRate(row) || 0}%
                    </span>
                  </div>
                )
              },
              {
                key: 'clickRate',
                title: 'Click Rate',
                sortable: true,
                render: (value, row) => (
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px'
                  }}>
                    <FiMousePointer style={{ color: '#6b7280', fontSize: '14px' }} />
                    <span style={{ 
                      color: '#111827', 
                      fontWeight: '500',
                      fontSize: '0.9rem'
                    }}>
                      {calculateClickRate(row) || 0}%
                    </span>
                  </div>
                )
              }
            ]}
            actions={[
              {
                key: 'send',
                icon: <FiSend />,
                title: 'Send Campaign',
                variant: 'success',
                show: (row) => row.status === 'draft' && hasPermission('canCreateEmailCampaigns')
              },
              {
                key: 'analytics',
                icon: <FiBarChart2 />,
                title: 'View Analytics',
                variant: 'primary',
                show: () => hasPermission('canViewEmailCampaigns')
              },
              {
                key: 'details',
                icon: <FiEye />,
                title: 'View Campaign Details',
                variant: 'info',
                show: () => hasPermission('canViewEmailCampaigns')
              },
              {
                key: 'edit',
                icon: <FiEdit />,
                title: 'Edit Campaign',
                variant: 'secondary',
                show: () => hasPermission('canEditEmailCampaigns')
              },
              {
                key: 'delete',
                icon: <FiTrash2 />,
                title: 'Delete Campaign',
                variant: 'danger',
                show: (row) => row.status === 'draft' && hasPermission('canDeleteEmailCampaigns')
              }
            ]}
            onActionClick={async (actionKey, row, rowIndex) => {
              switch (actionKey) {
                case 'send':
                  await handleSendCampaign(row.campaignId);
                  break;
                case 'analytics':
                  // Check if user has permission to view campaigns
                  if (!hasPermission('canViewEmailCampaigns')) {
                    showToast.error('You do not have permission to view campaign analytics');
                    return;
                  }
                  try {
                    const [campaignResponse, analyticsResponse] = await Promise.all([
                      axiosInstance.get(`/campaigns/${row.campaignId}`),
                      axiosInstance.get(`/campaigns/${row.campaignId}/analytics`)
                    ]);
                    setSelectedCampaign(campaignResponse.data);
                    setAnalyticsData(analyticsResponse.data);
                    setShowAnalyticsModal(true);
                  } catch (error) {
                    console.error('Error fetching campaign analytics:', error);
                    showToast.error('Failed to load campaign analytics');
                    setSelectedCampaign(row);
                    setAnalyticsData(null);
                    setShowAnalyticsModal(true);
                  }
                  break;
                case 'details':
                  // Check if user has permission to view campaigns
                  if (!hasPermission('canViewEmailCampaigns')) {
                    showToast.error('You do not have permission to view campaign details');
                    return;
                  }
                  try {
                    const campaignResponse = await axiosInstance.get(`/campaigns/${row.campaignId}`);
                    setSelectedCampaign(campaignResponse.data?.data || row);
                    setShowDetailsModal(true);
                  } catch (error) {
                    console.error('Error fetching campaign details:', error);
                    showToast.error('Failed to load campaign details');
                    setSelectedCampaign(row);
                    setShowDetailsModal(true);
                  }
                  break;
                case 'edit':
                  // Check if user has permission to edit campaigns
                  if (!hasPermission('canEditEmailCampaigns')) {
                    showToast.error('You do not have permission to edit campaigns');
                    return;
                  }
                  setSelectedCampaign(row);
                  setFormData({
                    name: row.name,
                    subject: row.subject,
                    content: row.content,
                    recipients: row.recipients,
                    scheduleDate: row.scheduleDate ? new Date(row.scheduleDate).toISOString().split('T')[0] : '',
                    settings: row.settings,
                    tags: row.tags
                  });
                  setShowCreateModal(true);
                  break;
                case 'delete':
                  await handleDeleteCampaign(row.campaignId);
                  break;
                default:
              }
            }}
            loading={loading || (!hasLoaded && campaigns.length === 0)}
            loadingMessage="Loading campaigns..."
            searchable={false} // We handle search in header
            filterable={false}
            sortable={true}
            pagination={false} // We handle pagination separately
            searchPlaceholder="Search campaigns..."
            emptyMessage="No campaigns found"
            striped={true}
            hover={true}
            responsive={true}
            sortConfig={sortConfig}
            onSort={handleSort}
            style={{ 
              boxShadow: 'none',
              borderRadius: '0',
              border: 'none',
              background: 'transparent'
            }}
              />
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '20px',
        background: '#ffffff',
        bottom: 0,
        borderTop: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '20px' 
        }}>
          <button
            onClick={resetSort}
            style={{
              padding: "8px 16px",
              backgroundColor: "#4f46e5",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              transition: "background-color 0.2s ease"
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "#4338ca"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "#4f46e5"}
          >
            Reset Sort
          </button>
          <span style={{ 
            color: '#6b7280', 
            fontSize: '14px' 
          }}>
            • {countData} results
          </span>
        </div>
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px' 
        }}>
          <span style={{ 
            color: '#6b7280', 
            fontSize: '14px' 
          }}>
            Rows per page:
          </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
            style={{
              padding: '4px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          
          <span style={{ 
            color: '#6b7280', 
            fontSize: '14px' 
          }}>
            {indexOfFirstItem + 1}-{Math.min(indexOfFirstItem + rowsPerPage, countData)} of {countData}
          </span>
          
          <button 
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            style={{
              padding: '6px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: page === 0 ? '#f9fafb' : '#ffffff',
              color: page === 0 ? '#9ca3af' : '#374151',
              cursor: page === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Previous
          </button>
          
          <button 
            disabled={page >= Math.ceil(countData / rowsPerPage) - 1}
            onClick={() => setPage(page + 1)}
            style={{
              padding: '6px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: page >= Math.ceil(countData / rowsPerPage) - 1 ? '#f9fafb' : '#ffffff',
              color: page >= Math.ceil(countData / rowsPerPage) - 1 ? '#9ca3af' : '#374151',
              cursor: page >= Math.ceil(countData / rowsPerPage) - 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Next
          </button>
        </div>
      </div>

      {/* Create/Edit Campaign Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{selectedCampaign ? 'Edit Campaign' : 'Create New Campaign'}</h2>
              <button onClick={() => {
                setShowCreateModal(false);
                setSelectedCampaign(null);
                resetForm();
              }}>×</button>
            </div>
            <form onSubmit={handleCreateCampaign} className="campaign-form">
              <div className="form-group">
                <label>Campaign Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Content *</label>
                <div className="content-section">
                  <div className="content-header">
                    <span>Email Content</span>
                    <button 
                      type="button" 
                      className="select-template-btn"
                      onClick={() => {
                        setShowTemplateSelector(true);
                        fetchTemplates();
                      }}
                    >
                      <FiFileText /> Use Template
                    </button>
                  </div>
                  {selectedTemplate && (
                    <div className="selected-template-info">
                      <span className="template-name">Using template: {selectedTemplate.templateName}</span>
                      <button 
                        type="button" 
                        className="clear-template-btn"
                        onClick={() => {
                          setSelectedTemplate(null);
                          setFormData(prev => ({ ...prev, content: '' }));
                        }}
                      >
                        Clear Template
                      </button>
                    </div>
                  )}
                  <div className="content-editor">
                    <div className="editor-tabs">
                      <button 
                        type="button" 
                        className={`tab-btn ${!showHtmlEditor ? 'active' : ''}`}
                        onClick={() => setShowHtmlEditor(false)}
                      >
                        Preview
                      </button>
                      <button 
                        type="button" 
                        className={`tab-btn ${showHtmlEditor ? 'active' : ''}`}
                        onClick={() => setShowHtmlEditor(true)}
                      >
                        HTML Editor
                      </button>
                    </div>
                    
                    {showHtmlEditor ? (
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                        rows={12}
                        required
                        placeholder="Enter your email content or select a template..."
                        className="html-editor"
                      />
                    ) : (
                      <div className="content-preview">
                        {formData.content ? (
                          <div 
                            dangerouslySetInnerHTML={{ __html: formData.content }}
                            className="preview-content"
                          />
                        ) : (
                          <div className="empty-preview">
                            <p>No content to preview. Enter content or select a template.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label>Recipients *</label>
                <div className="recipients-section">
                  <div className="recipients-header">
                    <span>Added Recipients ({formData.recipients.length})</span>
                    <button 
                      type="button" 
                      className="select-users-btn"
                      onClick={() => {
                        setShowBatchSelector(true);
                        fetchBatches();
                      }}
                    >
                      <FiUsers /> Select from Batches
                    </button>
                  </div>
                  
                  {formData.recipients.map((recipient, index) => (
                    <div key={index} className="recipient-row">
                      <input
                        type="email"
                        placeholder="Email"
                        value={recipient.email}
                        onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                        required
                      />
                      <input
                        type="text"
                        placeholder="Name (optional)"
                        value={recipient.name}
                        onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                      />
                      {formData.recipients.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => removeRecipient(index)}
                          className="remove-btn"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addRecipient} className="add-recipient-btn">
                    <FiPlus /> Add Recipient
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label>Campaign Type *</label>
                <div className="campaign-type-selector">
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="campaignType"
                        value="manual"
                        checked={formData.isManual === true}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          isManual: true, 
                          isScheduled: false,
                          scheduleDate: '' 
                        }))}
                      />
                      <div className="radio-label">
                        <div className="radio-label-row">
                          <FiSend className="radio-icon" />
                          <span className="radio-label-text">Send Immediately</span>
                        </div>
                        <small>Send the campaign right away</small>
                      </div>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="campaignType"
                        value="scheduled"
                        checked={formData.isScheduled === true}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          isManual: false, 
                          isScheduled: true 
                        }))}
                      />
                      <div className="radio-label">
                        <div className="radio-label-row">
                          <FiClock className="radio-icon" />
                          <span className="radio-label-text">Schedule for Later</span>
                        </div>
                        <small>Schedule the campaign for a specific date and time</small>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              {formData.isScheduled && (
                <div className="form-group">
                  <label>Schedule Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduleDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduleDate: e.target.value }))}
                    required
                    min={new Date().toISOString().slice(0, 16)}
                  />
                  <small className="field-hint">Select when you want this campaign to be sent</small>
                </div>
              )}
              
              <div className="form-sections">
                <div className="form-section">
                  <h3>Campaign Settings</h3>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.settings.trackOpens}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          settings: { ...prev.settings, trackOpens: e.target.checked }
                        }))}
                      />
                      Track email opens
                    </label>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.settings.trackClicks}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          settings: { ...prev.settings, trackClicks: e.target.checked }
                        }))}
                      />
                      Track link clicks
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={() => {
                  setShowCreateModal(false);
                  setSelectedCampaign(null);
                  resetForm();
                }}>
                  Cancel
                </button>
                <button type="submit">
                  {selectedCampaign ? 'Update Campaign' : 'Create Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Batch Selector Modal */}
      {showBatchSelector && (
        <div className="modal-overlay">
          <div className="modal batch-selector-modal">
            <div className="modal-header">
              <h2>Select Recipients from Batches</h2>
              <button onClick={() => {
                setShowBatchSelector(false);
                setBatchSearch('');
                setSelectedBatches([]);
              }}>×</button>
            </div>
            <div className="batch-selector-content">
              {/* Search Section */}
              <div className="search-section">
                <div className="search-box">
                  <FiSearch />
                  <input
                    type="text"
                    placeholder="Search batches by name..."
                    value={batchSearch}
                    onChange={(e) => {
                      setBatchSearch(e.target.value);
                      fetchBatches(e.target.value);
                    }}
                  />
                </div>
              </div>
              
              {/* Two Column Layout */}
              <div className="two-column-layout">
                {/* Left Column - Available Batches */}
                <div className="left-column">
                  <div className="column-header">
                    <h3>Available Batches</h3>
                    <div className="search-results-count">
                      {loadingBatches ? 'Loading...' : `${batches.length} batches found`}
                    </div>
                  </div>
                  
                  <div className="batches-list">
                    {loadingBatches ? (
                      <div className="loading">
                        <div className="loading-spinner"></div>
                        <p>Loading batches...</p>
                      </div>
                    ) : batches.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-icon">
                          <FiUsers />
                        </div>
                        <h3>No batches found</h3>
                        <p>Try adjusting your search criteria or create a new batch</p>
                      </div>
                    ) : (
                      batches.map((batch) => (
                        <div 
                          key={batch.batchId || batch._id} 
                          className={`batch-item ${selectedBatches.some(b => (b.batchId === batch.batchId || b._id === batch._id)) ? 'selected' : ''}`} 
                          onClick={() => selectBatch(batch)}
                        >
                          <div className="batch-info">
                            <div className="batch-name">{batch.batchedName}</div>
                            <div className="batch-details">
                              <span className="batch-count">
                                {batch.personDetails?.length || batch.totalRecipients || 0} recipients
                              </span>
                              {batch.createdAt && (
                                <span className="batch-date">
                                  {new Date(batch.createdAt).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="batch-action">
                            {selectedBatches.some(b => (b.batchId === batch.batchId || b._id === batch._id)) ? (
                              <FiCheckCircle className="check-icon" />
                            ) : (
                              <FiPlus />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Right Column - Selected Batches */}
                <div className="right-column">
                  <div className="column-header">
                    <h3>Selected Batches</h3>
                    <div className="selected-count">
                      {selectedBatches.length} selected
                    </div>
                  </div>
                  
                  {selectedBatches.length > 0 ? (
                    <div className="selected-batches-list">
                      {selectedBatches.map((batch) => {
                        const batchId = batch.batchId || batch._id;
                        const totalEmails = batch.personDetails?.length || batch.totalRecipients || 0;
                        return (
                          <div key={batchId} className="selected-batch-item">
                            <div className="selected-batch-info">
                              <div className="selected-batch-name">{batch.batchedName}</div>
                              <div className="selected-batch-count">{totalEmails} recipients</div>
                            </div>
                            <button 
                              className="remove-batch-btn"
                              onClick={() => removeSelectedBatch(batchId)}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="empty-selected">
                      <FiUsers className="empty-icon" />
                      <p>No batches selected</p>
                      <span>Click on batches to select them</span>
                    </div>
                  )}
                  
                  {selectedBatches.length > 0 && (
                    <div className="selected-actions">
                      <button 
                        className="clear-all-btn"
                        onClick={() => setSelectedBatches([])}
                      >
                        Clear All
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Action Buttons */}
              <div className="modal-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => {
                    setShowBatchSelector(false);
                    setBatchSearch('');
                    setSelectedBatches([]);
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="add-selected-btn"
                  onClick={addSelectedBatchesToRecipients}
                  disabled={selectedBatches.length === 0}
                >
                  Add Recipients from {selectedBatches.length > 0 ? `(${selectedBatches.length})` : ''} Batch{selectedBatches.length !== 1 ? 'es' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && selectedCampaign && (
        <div className="modal-overlay">
          <div className="modal analytics-modal">
            <div className="modal-header">
              <h2>Campaign Analytics: {selectedCampaign.name}</h2>
              <button onClick={() => {
                setShowAnalyticsModal(false);
                setSelectedCampaign(null);
                setAnalyticsData(null);
              }}>×</button>
            </div>
            <div className="analytics-content" style={{ 
              maxHeight: 'calc(95vh - 120px)', 
              overflowY: 'auto',
              padding: '20px'
            }}>
              {/* Compact Analytics Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '15px', 
                marginBottom: '20px' 
              }}>
                {/* Delivery Statistics Card */}
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '8px',
                  padding: '15px',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                  minHeight: '120px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <FiSend style={{ fontSize: '16px' }} />
                    <h3 style={{ margin: '0', fontSize: '0.9rem', fontWeight: '600' }}>Delivery Statistics</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {selectedCampaign.totalRecipients || selectedCampaign.recipients?.length || 0}
                  </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Total Recipients</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {selectedCampaign.sentCount || 0}
                    </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Sent</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {selectedCampaign.failedCount || 0}
                      </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Failed</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {selectedCampaign.recipients?.filter(r => r.status === 'pending').length || 0}
                      </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Pending</div>
                    </div>
                  </div>
                </div>
                
                {/* Engagement Metrics Card */}
                <div style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  borderRadius: '8px',
                  padding: '15px',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(240, 147, 251, 0.3)',
                  minHeight: '120px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <FiBarChart2 style={{ fontSize: '16px' }} />
                    <h3 style={{ margin: '0', fontSize: '0.9rem', fontWeight: '600' }}>Engagement Metrics</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {selectedCampaign.openCount || 0}
                  </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Total Opens</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {calculateOpenRate(selectedCampaign)}%
                  </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Open Rate</div>
                  </div>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {selectedCampaign.clickCount || 0}
                    </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Total Clicks</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {calculateClickRate(selectedCampaign)}%
                        </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Click Rate</div>
                    </div>
                </div>
              </div>
              
                {/* Performance Summary Card */}
                <div style={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  borderRadius: '8px',
                  padding: '15px',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(79, 172, 254, 0.3)',
                  minHeight: '120px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <FiTrendingUp style={{ fontSize: '16px' }} />
                    <h3 style={{ margin: '0', fontSize: '0.9rem', fontWeight: '600' }}>Performance Summary</h3>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {calculateDeliveryRate(selectedCampaign)}%
                        </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Delivery Rate</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {calculateClickToOpenRate(selectedCampaign)}%
                        </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Click-to-Open</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {selectedCampaign.status?.toUpperCase() || 'UNKNOWN'}
                    </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Status</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>
                        {selectedCampaign.updatedAt ? new Date(selectedCampaign.updatedAt).toLocaleDateString() : 'N/A'}
                  </div>
                      <div style={{ fontSize: '10px', opacity: '0.9' }}>Last Updated</div>
                </div>
              </div>
                  </div>
                </div>
                
              {/* Recipients Table using UniversalTable */}
              <div style={{ 
                background: '#ffffff',
                borderRadius: '12px',
                padding: '10px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                width: '100%'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '10px' 
                }}>
                  <h3 style={{ 
                    margin: '0', 
                    color: '#1f2937', 
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <FiUsers style={{ color: '#3b82f6' }} /> 
                    Recipients Performance
                  </h3>
                  
                    </div>
                
                <UniversalTable
                  className="universal-table"
                  data={Array.isArray(selectedCampaign.recipients) 
                    ? selectedCampaign.recipients.map(recipient => ({
                        email: recipient.email || '',
                        name: recipient.name || '',
                        status: recipient.status || 'pending',
                        sentAt: recipient.sentAt || null,
                        openCount: recipient.openCount || 0,
                        clickCount: recipient.clickCount || 0,
                        firstOpenedAt: recipient.firstOpenedAt || null,
                        lastOpenedAt: recipient.lastOpenedAt || null,
                        firstClickedAt: recipient.firstClickedAt || null,
                        lastClickedAt: recipient.lastClickedAt || null
                      }))
                    : []}
                  columns={[
                    {
                      key: 'email',
                      title: 'Email Address',
                      type: 'email',
                      sortable: false,
                      filterable: false
                    },
                    {
                      key: 'name',
                      title: 'Name',
                      type: 'name',
                      sortable: false,
                      filterable: false,
                      render: (value) => value || '-'
                    },
                    {
                      key: 'status',
                      title: 'Status',
                      type: 'status',
                      sortable: false,
                      filterable: false
                    },
                    {
                      key: 'sentAt',
                      title: 'Sent At',
                      type: 'datetime',
                      sortable: false,
                      render: (value) => value ? new Date(value).toLocaleString() : '-'
                    },
                    {
                      key: 'openCount',
                      title: 'Opens',
                      type: 'number',
                      sortable: false,
                      render: (value) => value || 0
                    },
                    {
                      key: 'clickCount',
                      title: 'Clicks',
                      type: 'number',
                      sortable: false,
                      render: (value) => value || 0
                    },
                    {
                      key: 'engagement',
                      title: 'Engagement',
                      type: 'number',
                      sortable: false,
                      render: (value, row) => {
                        const engagement = calculateRecipientEngagement(row);
                        return `${engagement}%`;
                      }
                    }
                  ]}
                  actions={[]}
                  searchable={false}
                  filterable={false}
                  sortable={false}
                  pagination={false}
                  pageSize={10}
                  emptyMessage="No recipients found for this campaign"
                  compact={true}
                  striped={false}
                  hover={true}
                  responsive={true}
                  showRowNumbers={false}
                  variant="default"
                  style={{ 
                    boxShadow: 'none',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    height: 'auto',
                    maxHeight: '400px',
                    overflow: 'hidden',
                    width: '100%',
                    minWidth: '100%'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Details Modal */}
      {showDetailsModal && selectedCampaign && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header">
              <h2>Campaign Details: {selectedCampaign.name || 'Untitled Campaign'}</h2>
              <button onClick={() => {
                setShowDetailsModal(false);
                setSelectedCampaign(null);
              }}>×</button>
            </div>
            <div style={{ 
              maxHeight: 'calc(95vh - 120px)', 
              overflowY: 'auto',
              padding: '20px'
            }}>
              {/* Campaign Information */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px',
                  marginBottom: '15px',
                  color: '#1f2937',
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  <FiMail style={{ color: '#667eea' }} />
                  Campaign Information
                </h3>
                
                <div style={{
                  background: '#f9fafb',
                  borderRadius: '8px',
                  padding: '20px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '0.85rem', 
                        fontWeight: '600', 
                        color: '#6b7280',
                        marginBottom: '5px'
                      }}>Campaign Name</label>
                      <div style={{ 
                        fontSize: '0.95rem', 
                        color: '#1f2937',
                        fontWeight: '500'
                      }}>{selectedCampaign.name || '-'}</div>
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '0.85rem', 
                        fontWeight: '600', 
                        color: '#6b7280',
                        marginBottom: '5px'
                      }}>Subject</label>
                      <div style={{ 
                        fontSize: '0.95rem', 
                        color: '#1f2937',
                        fontWeight: '500'
                      }}>{selectedCampaign.subject || '-'}</div>
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '0.85rem', 
                        fontWeight: '600', 
                        color: '#6b7280',
                        marginBottom: '5px'
                      }}>Status</label>
                      <div>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          backgroundColor: selectedCampaign.status === 'sent' ? '#d1fae5' : 
                                         selectedCampaign.status === 'draft' ? '#fef3c7' : 
                                         selectedCampaign.status === 'scheduled' ? '#dbeafe' : '#fee2e2',
                          color: selectedCampaign.status === 'sent' ? '#065f46' : 
                                selectedCampaign.status === 'draft' ? '#92400e' : 
                                selectedCampaign.status === 'scheduled' ? '#1e40af' : '#991b1b'
                        }}>
                          {selectedCampaign.status ? selectedCampaign.status.charAt(0).toUpperCase() + selectedCampaign.status.slice(1) : 'Unknown'}
                        </span>
                      </div>
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: '0.85rem', 
                        fontWeight: '600', 
                        color: '#6b7280',
                        marginBottom: '5px'
                      }}>
                        <FiUsers />
                        Recipients
                      </label>
                      <div style={{ 
                        fontSize: '0.95rem', 
                        color: '#1f2937',
                        fontWeight: '500'
                      }}>
                        {selectedCampaign.totalRecipients || selectedCampaign.recipients?.length || 0} recipient(s)
                      </div>
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: '0.85rem', 
                        fontWeight: '600', 
                        color: '#6b7280',
                        marginBottom: '5px'
                      }}>
                        <FiClock />
                        Created
                      </label>
                      <div style={{ 
                        fontSize: '0.95rem', 
                        color: '#1f2937',
                        fontWeight: '500'
                      }}>
                        {selectedCampaign.createdAt ? new Date(selectedCampaign.createdAt).toLocaleString() : '-'}
                      </div>
                    </div>
                    
                    {selectedCampaign.scheduleDate && (
                      <div>
                        <label style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: '5px',
                          fontSize: '0.85rem', 
                          fontWeight: '600', 
                          color: '#6b7280',
                          marginBottom: '5px'
                        }}>
                          <FiCalendar />
                          Scheduled For
                        </label>
                        <div style={{ 
                          fontSize: '0.95rem', 
                          color: '#1f2937',
                          fontWeight: '500'
                        }}>
                          {new Date(selectedCampaign.scheduleDate).toLocaleString()}
                        </div>
                      </div>
                    )}
                    
                    {selectedCampaign.updatedAt && (
                      <div>
                        <label style={{ 
                          fontSize: '0.85rem', 
                          fontWeight: '600', 
                          color: '#6b7280',
                          marginBottom: '5px'
                        }}>Last Updated</label>
                        <div style={{ 
                          fontSize: '0.95rem', 
                          color: '#1f2937',
                          fontWeight: '500'
                        }}>
                          {new Date(selectedCampaign.updatedAt).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Campaign Content Preview */}
              {selectedCampaign.content && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    marginBottom: '15px',
                    color: '#1f2937',
                    fontSize: '1.1rem',
                    fontWeight: '600'
                  }}>
                    <FiFileText style={{ color: '#667eea' }} />
                    Email Content Preview
                  </h3>
                  <div style={{
                    background: '#f9fafb',
                    borderRadius: '8px',
                    padding: '20px',
                    border: '1px solid #e5e7eb',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    <div 
                      dangerouslySetInnerHTML={{ __html: selectedCampaign.content || selectedCampaign.html || 'No content available' }}
                      style={{
                        fontSize: '0.9rem',
                        lineHeight: '1.6',
                        color: '#374151'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Recipients List */}
              {selectedCampaign.recipients && Array.isArray(selectedCampaign.recipients) && selectedCampaign.recipients.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    marginBottom: '15px',
                    color: '#1f2937',
                    fontSize: '1.1rem',
                    fontWeight: '600'
                  }}>
                    <FiUsers style={{ color: '#667eea' }} />
                    Recipients List ({selectedCampaign.recipients.length})
                  </h3>
                  <div style={{
                    background: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ 
                        background: '#f9fafb',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1
                      }}>
                        <tr>
                          <th style={{ 
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#6b7280',
                            borderBottom: '1px solid #e5e7eb'
                          }}>Name</th>
                          <th style={{ 
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#6b7280',
                            borderBottom: '1px solid #e5e7eb'
                          }}>Email</th>
                          <th style={{ 
                            padding: '12px',
                            textAlign: 'left',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#6b7280',
                            borderBottom: '1px solid #e5e7eb'
                          }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCampaign.recipients.map((recipient, index) => (
                          <tr key={index} style={{ 
                            borderBottom: '1px solid #f3f4f6',
                            ':hover': { backgroundColor: '#f9fafb' }
                          }}>
                            <td style={{ padding: '12px', fontSize: '0.9rem', color: '#1f2937' }}>
                              {recipient.name || '-'}
                            </td>
                            <td style={{ padding: '12px', fontSize: '0.9rem', color: '#1f2937' }}>
                              {recipient.email || '-'}
                            </td>
                            <td style={{ padding: '12px' }}>
                              <span style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                borderRadius: '8px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                backgroundColor: recipient.status === 'sent' ? '#d1fae5' : 
                                               recipient.status === 'pending' ? '#fef3c7' : '#fee2e2',
                                color: recipient.status === 'sent' ? '#065f46' : 
                                      recipient.status === 'pending' ? '#92400e' : '#991b1b'
                              }}>
                                {recipient.status || 'pending'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tags */}
              {selectedCampaign.tags && Array.isArray(selectedCampaign.tags) && selectedCampaign.tags.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    marginBottom: '15px',
                    color: '#1f2937',
                    fontSize: '1.1rem',
                    fontWeight: '600'
                  }}>
                    Tags
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedCampaign.tags.map((tag, index) => (
                      <span key={index} style={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        backgroundColor: '#e0e7ff',
                        color: '#4338ca'
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ 
              padding: '15px 20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button 
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedCampaign(null);
                }}
                style={{
                  padding: '8px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  background: '#ffffff',
                  color: '#374151',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <div className="modal-overlay template-selector-overlay">
          <div className="modal template-selector-modal enhanced">
            <div className="modal-header enhanced-header">
              <div className="header-content">
                <div className="header-icon">
                  <FiFileText />
                </div>
                <div className="header-text">
                  <h2>Select Email Template</h2>
                  <p>Choose from your saved templates or create a new one</p>
                </div>
              </div>
              
              <button 
                className="close-btn enhanced"
                onClick={() => {
                  setShowTemplateSelector(false);
                  setTemplateSearch('');
                }}
              >
                <FiXCircle />
              </button>
            </div>
            <div className="template-selector-content enhanced">
              {/* Single Line Search and Filter Section */}
              <div className="search-section enhanced single-line">
                <div className="search-box enhanced">
                  <FiSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search templates by name, content, or tags..."
                    value={templateSearch}
                    onChange={(e) => {
                      setTemplateSearch(e.target.value);
                      fetchTemplates(e.target.value);
                    }}
                  />
                  {templateSearch && (
                    <button 
                      className="clear-search"
                      onClick={() => {
                        setTemplateSearch('');
                        fetchTemplates('');
                      }}
                    >
                      <FiXCircle />
                    </button>
                  )}
                </div>
                
                <select 
                  className="template-filter-select"
                  value={templateFilter}
                  onChange={(e) => setTemplateFilter(e.target.value)}
                >
                  <option value="all">Modern Templates</option>
                  <option value="html">HTML Templates</option>
                  <option value="text">Text Templates</option>
                  <option value="favorites">Favorites</option>
                  <option value="recent">Recent (7 days)</option>
                </select>
                
                <select 
                  className="template-sort-select"
                  value={templateSort}
                  onChange={(e) => setTemplateSort(e.target.value)}
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="favorites">Favorites First</option>
                </select>
                
                <div className="view-mode-toggle">
                  <button 
                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                  >
                    <FiBarChart2 />
                  </button>
                  <button 
                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List View"
                  >
                    <FiFileText />
                  </button>
                </div>
                
                <button 
                  className="create-test-template-btn enhanced"
                  onClick={createTestTemplate}
                  title="Create Test Template"
                >
                  <FiPlus /> Create Test Template
                </button>
                
                        <button 
                          className="refresh-templates-btn"
                          onClick={() => fetchTemplates(templateSearch)}
                          title="Refresh Templates"
                        >
                          <FiRefreshCw />
                        </button>
                        
                        <button 
                          className="debug-templates-btn"
                          onClick={async () => {
                            try {
                              let userId = localStorage.getItem('userId') || 
                                           localStorage.getItem('superadminId') || 
                                           localStorage.getItem('id') ||
                                           localStorage.getItem('UserId');
                              
                              const response = await axiosInstance.get(`/templates/user/${userId}?limit=100&includeContent=true`);
                              
                              if (response && response.data && response.data.data && response.data.data.length > 0) {
                                response.data.data.forEach((template, index) => {
                                  // Template processing logic here
                                });
                              }
                              
                              alert(`Debug Complete!\n\nTotal Templates: ${response?.data?.data?.length || 0}\nCheck console for details.`);
                            } catch (error) {
                              console.error('Debug fetch error:', error);
                              alert(`Debug Error: ${error.message}\nCheck console for details.`);
                            }
                          }}
                          title="Debug Template Fetch"
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            minWidth: 'auto'
                          }}
                        >
                          🔍
                        </button>
              </div>
              
              {/* Template Count Display */}
              <div className="template-count-display">
                <div className="count-item total">
                  <FiFileText />
                  <span>Total Templates:</span>
                  <span className="count-number">{templates.length}</span>
                </div>
                <div className="count-item filtered">
                  <FiSearch />
                  <span>Filtered Results:</span>
                  <span className="count-number">{getFilteredAndSortedTemplates().length}</span>
                </div>
                {selectedTemplate && (
                  <div className="count-item selected">
                    <FiCheckCircle />
                    <span>Selected:</span>
                    <span className="count-number">1</span>
                  </div>
                )}
              </div>
              
              {/* Enhanced Templates Grid */}
              <div className={`templates-grid enhanced ${viewMode}-view`}>
                {loadingTemplates ? (
                  <div className="loading enhanced">
                    <div className="loading-spinner"></div>
                    <p>Loading templates...</p>
                  </div>
                ) : templates.length === 0 ? (
                  <div className="empty-state enhanced">
                    <div className="empty-icon">
                      <FiFileText />
                    </div>
                    <h3>No Templates Available</h3>
                    <p>You don't have any email templates yet. Create your first template to start building amazing email campaigns.</p>
                    <div className="empty-actions">
                      <button 
                        className="create-first-template-btn primary"
                        onClick={createTestTemplate}
                      >
                        <FiPlus /> Create Your First Template
                      </button>
                      <button 
                        className="create-first-template-btn secondary"
                        onClick={() => {
                          setShowTemplateSelector(false);
                          // Navigate to template builder or library
                        }}
                      >
                        <FiFileText /> Browse Template Library
                      </button>
                    </div>
                    <div className="empty-tips">
                      <h4>💡 Quick Tips:</h4>
                      <ul>
                        <li>Start with a simple text template</li>
                        <li>Use the Modern Email Builder for HTML templates</li>
                        <li>Save your templates for reuse in future campaigns</li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  getFilteredAndSortedTemplates().map((template, index) => (
                    <div 
                      key={template.TemplateId || template._id} 
                      className={`template-card enhanced ${selectedTemplate?.TemplateId === template.TemplateId || selectedTemplate?._id === template._id ? 'selected' : ''}`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <div className="template-card-header">
                        <div className="template-badge">
                          {template.html ? (
                            <span className="badge html">
                              <FiFileText /> HTML
                            </span>
                          ) : (
                            <span className="badge text">
                              <FiFileText /> Text
                            </span>
                          )}
                        </div>
                        <div className="template-header-actions">
                          <button 
                            className={`favorite-btn ${favoriteTemplates.includes(template.TemplateId || template._id) ? 'favorited' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(template.TemplateId || template._id);
                            }}
                            title={favoriteTemplates.includes(template.TemplateId || template._id) ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <FiAward />
                          </button>
                          <div className="template-date">
                            {new Date(template.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="template-card-content">
                        <h3 className="template-name">{template.templateName}</h3>
                        
                        {/* Template Info */}
                        <div className="template-info">
                          <div className="template-meta">
                            <span className="template-type">
                              {template.html ? 'HTML Template' : 'Text Template'}
                            </span>
                            <span className="template-size">
                              {template.html ? 
                                `${Math.round((template.html.length / 1024) * 10) / 10} KB` : 
                                `${Math.round((template.content?.length / 1024) * 10) / 10} KB`
                              }
                            </span>
                          </div>
                          <div className="template-stats">
                            <span className="stat-item">
                              <FiCalendar />
                              {new Date(template.createdAt).toLocaleDateString()}
                            </span>
                            {template.updatedAt && (
                              <span className="stat-item">
                                <FiRefreshCw />
                                Updated {new Date(template.updatedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="template-preview enhanced">
                          {(() => {
                            // Get content from multiple possible sources
                            let content = template.html || template.content || template.body || '';
                            
                            // If we have JSON content, try to convert it
                            if (!content && template.json && template.json.sections) {
                              content = convertJsonToHtml(template.json);
                            }
                            
                            // If we have content, display it
                            if (content && content.trim()) {
                              // Check if it's HTML content
                              if (content.includes('<') && content.includes('>')) {
                                return (
                                  <div className="preview-container">
                                    <div 
                                      className="preview-content"
                                      dangerouslySetInnerHTML={{ 
                                        __html: (() => {
                                          let processedHtml = content.substring(0, 200);
                                          const baseURL = 'http://localhost:5000';
                                          
                                          // Fix image URLs for preview
                                          processedHtml = processedHtml.replace(
                                            /src="(\/uploads\/[^"]+)"/g, 
                                            `src="${baseURL}$1"`
                                          );
                                          processedHtml = processedHtml.replace(
                                            /src="(uploads\/[^"]+)"/g, 
                                            `src="${baseURL}/$1"`
                                          );
                                          processedHtml = processedHtml.replace(
                                            /src="(\/cdn\/[^"]+)"/g, 
                                            `src="${baseURL}$1"`
                                          );
                                          processedHtml = processedHtml.replace(
                                            /src="(cdn\/[^"]+)"/g, 
                                            `src="${baseURL}/$1"`
                                          );
                                          processedHtml = processedHtml.replace(
                                            /src="(\/v1\/cdn\/[^"]+)"/g, 
                                            `src="${baseURL}$1"`
                                          );
                                          processedHtml = processedHtml.replace(
                                            /src="(v1\/cdn\/[^"]+)"/g, 
                                            `src="${baseURL}/$1"`
                                          );
                                          processedHtml = processedHtml.replace(
                                            /src="(?!http|https|data:)([^"]+)"/g, 
                                            `src="${baseURL}/$1"`
                                          );
                                          
                                          return processedHtml;
                                        })()
                                      }} 
                                    />
                                    {content.length > 200 && (
                                      <div className="preview-overlay">
                                        <span>Click to see full preview</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              } else {
                                // Text content
                                return (
                                  <div className="text-preview">
                                    <p>{content.substring(0, 150)}...</p>
                                  </div>
                                );
                              }
                            } else {
                              // No content found
                              return (
                                <div className="text-preview">
                                  <p className="no-content">No content preview available</p>
                                  <div className="debug-info" style={{fontSize: '0.7rem', color: '#999', marginTop: '0.5rem'}}>
                                    Debug: html={!!template.html}, content={!!template.content}, json={!!template.json}
                                  </div>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      </div>
                      
                      <div className="template-card-actions">
                        <button 
                          className="action-btn preview enhanced"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewTemplateData(template);
                            setShowFullPreview(true);
                          }}
                          title="Preview Template"
                        >
                          <FiEye />
                          <span>Preview</span>
                        </button>
                        <button 
                          className="action-btn select enhanced primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            selectTemplate(template);
                          }}
                          title="Use This Template"
                        >
                          <FiFileText />
                          <span>Use Template</span>
                        </button>
                        
                        <button 
                          className="action-btn debug"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          title="Debug Template Content"
                          style={{
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            minWidth: 'auto'
                          }}
                        >
                          🐛
                        </button>
                      </div>
                      
                      {selectedTemplate?.TemplateId === template.TemplateId || selectedTemplate?._id === template._id ? (
                        <div className="selection-indicator">
                          <FiCheckCircle />
                          <span>Selected</span>
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
              
              {/* Modal Footer */}
              <div className="modal-footer">
                <div className="footer-info">
                  <span className="template-count">
                    {getFilteredAndSortedTemplates().length} template{getFilteredAndSortedTemplates().length !== 1 ? 's' : ''} found
                  </span>
                </div>
                <div className="footer-actions">
                  <button 
                    className="cancel-btn"
                    onClick={() => {
                      setShowTemplateSelector(false);
                      setTemplateSearch('');
                      setSelectedTemplate(null);
                    }}
                  >
                    Cancel
                  </button>
                  {selectedTemplate && (
                    <button 
                      className="confirm-btn"
                      onClick={() => selectTemplate(selectedTemplate)}
                    >
                      <FiFileText />
                      Use Selected Template
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full-Screen Template Preview Modal */}
      {showFullPreview && previewTemplateData && (
        <div className="modal-overlay fullscreen-preview">
          <div className="fullscreen-preview-modal">
            <div className="preview-header">
              <div className="preview-title">
                <h2>Template Preview: {previewTemplateData.templateName}</h2>
                <div className="preview-meta">
                  <span className="template-type">
                    {previewTemplateData.html ? 'HTML Template' : 'Text Template'}
                  </span>
                  <span className="template-date">
                    Created: {new Date(previewTemplateData.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <div className="preview-actions">
                <button 
                  className="action-btn secondary"
                  onClick={() => selectTemplate(previewTemplateData)}
                >
                  <FiFileText />
                  Use This Template
                </button>
                <button 
                  className="close-btn"
                  onClick={() => {
                    setShowFullPreview(false);
                    setPreviewTemplateData(null);
                  }}
                >
                  <FiXCircle />
                </button>
              </div>
            </div>
            
            <div className="preview-content">
              <div className="preview-iframe-container">
                <iframe
                  srcDoc={(() => {
                    const fullTemplate = previewTemplateData;
                    let processedHtml = fullTemplate.html || fullTemplate.content || 'No content available';
                    const baseURL = 'http://localhost:5000';
                    
                    // Fix image URLs
                    processedHtml = processedHtml.replace(
                      /src="(\/uploads\/[^"]+)"/g, 
                      `src="${baseURL}$1"`
                    );
                    processedHtml = processedHtml.replace(
                      /src="(uploads\/[^"]+)"/g, 
                      `src="${baseURL}/$1"`
                    );
                    processedHtml = processedHtml.replace(
                      /src="(\/cdn\/[^"]+)"/g, 
                      `src="${baseURL}$1"`
                    );
                    processedHtml = processedHtml.replace(
                      /src="(cdn\/[^"]+)"/g, 
                      `src="${baseURL}/$1"`
                    );
                    processedHtml = processedHtml.replace(
                      /src="(\/v1\/cdn\/[^"]+)"/g, 
                      `src="${baseURL}$1"`
                    );
                    processedHtml = processedHtml.replace(
                      /src="(v1\/cdn\/[^"]+)"/g, 
                      `src="${baseURL}/$1"`
                    );
                    processedHtml = processedHtml.replace(
                      /src="(?!http|https|data:)([^"]+)"/g, 
                      `src="${baseURL}/$1"`
                    );
                    
                    return `
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <meta charset="utf-8">
                          <meta name="viewport" content="width=device-width, initial-scale=1">
                          <style>
                            body { 
                              font-family: Arial, sans-serif; 
                              margin: 0; 
                              padding: 20px; 
                              background: #f9f9f9;
                              line-height: 1.6;
                            }
                            .email-container {
                              max-width: 600px;
                              margin: 0 auto;
                              background: white;
                              border-radius: 8px;
                              box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                              overflow: hidden;
                            }
                            .email-content {
                              padding: 30px;
                            }
                            img { 
                              max-width: 100%; 
                              height: auto; 
                              border-radius: 4px;
                            }
                          </style>
                        </head>
                        <body>
                          <div class="email-container">
                            <div class="email-content">
                              ${processedHtml}
                            </div>
                          </div>
                        </body>
                      </html>
                    `;
                  })()}
                  className="preview-iframe"
                  title="Template Preview"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCampaign;

